import type { DateTime } from "luxon";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import type { DropdownItem } from "../../../../../../../components/UIToolkit/Inputs/SystemDropDown";
import { useHospitalData } from "../../../../../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../../../../../hooks/useJobs";
import { isAffectByDayStart, isInPTO } from "../../../../../../../utils/dateUtils";
import type { DepartmentLocation, Nurse } from "../../../../../../../utils/models";
import { getUniqueLocations } from "../../../../../../../utils/utilFunctions";
import { useShiftPredictionWarnings } from "../../../../../../../utils/warningDialogs/useShiftPredictionWarnings";
import { DIFF_HOURS_WARNING } from "../../../../../../../utils/warningDialogs/utils";
import { buildEmployeeStatusWarning, isAgencyNurse } from "../../../../../../../utils/warningDialogs/WarningDialog";
import { useAddSingleShiftByDateAndNurse } from "../../hooks";

type Props = {
	nurseFromProfile: Nurse;
	selectedDate: DateTime;
	noLocationText: string;
	onCloseCalled: () => void;
};
export const useSingleShiftCreation = ({ nurseFromProfile, selectedDate, noLocationText, onCloseCalled }: Props) => {
	const { chosenDepartment, calculateDefaultTimesAndLocationForStaff, getNurseShiftsInTimeFrame } = useHospitalData();
	const enabledJobs = useJobs();

	const locations = useMemo(
		() =>
			[null, ...getUniqueLocations(chosenDepartment.locations, false).filter((value) => value.location)].map<DropdownItem<DepartmentLocation | null>>(
				(value) => ({
					label: () => value?.location ?? noLocationText,
					value: value,
					id: value?.id ?? noLocationText,
				}),
			),
		[chosenDepartment.locations, noLocationText],
	);

	const autoFillSuggestion = useMemo(() => {
		// Pick the best auto-fill suggestion, avoiding times already used on this date
		const suggestions = calculateDefaultTimesAndLocationForStaff(nurseFromProfile.id);
		if (suggestions.length === 0) return undefined;

		const allShifts = getNurseShiftsInTimeFrame(nurseFromProfile.id);
		const existingShiftsOnDate = allShifts.filter((shift) => shift.startTime.hasSame(selectedDate, "day"));

		const first = suggestions[0];
		const hasConflict = existingShiftsOnDate.some(
			(shift) => shift.startTime.toFormat("HH:mm") === first.startTime && shift.endTime.toFormat("HH:mm") === first.endTime,
		);

		if (hasConflict && suggestions.length > 1) {
			return suggestions[1];
		}
		return first;
	}, [calculateDefaultTimesAndLocationForStaff, getNurseShiftsInTimeFrame, nurseFromProfile.id, selectedDate]);

	const autoFillLocation = useMemo(() => {
		// Find the matching DepartmentLocation for the suggestion
		if (!autoFillSuggestion?.location) return undefined;
		return locations.find((loc) => loc.value?.location === autoFillSuggestion.location)?.value ?? undefined;
	}, [autoFillSuggestion, locations]);

	const [selectedLocation, setSelectedLocation] = useState<DepartmentLocation | null | undefined>(() => autoFillLocation);
	const nurseJobs = useMemo(() => {
		if (nurseFromProfile.jobs?.length) return nurseFromProfile.jobs;
		if (nurseFromProfile.jobIds?.length) return enabledJobs.filter((j) => nurseFromProfile.jobIds?.includes(j.id));
		return enabledJobs;
	}, [nurseFromProfile.jobs, nurseFromProfile.jobIds, enabledJobs]);
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>(nurseJobs[0]?.id);

	const [shiftTimes, setShiftTimes] = useState<{ startTime: DateTime; endTime: DateTime }>(() => {
		if (autoFillSuggestion) {
			const [startHour, startMinute] = autoFillSuggestion.startTime.split(":").map(Number);
			const [endHour, endMinute] = autoFillSuggestion.endTime.split(":").map(Number);
			// Scheduled grace, or a 06:30 shift on a 07:00 department is created on the following day.
			const grace = chosenDepartment.scheduled_shift_delta_minutes;
			const startTime = isAffectByDayStart(selectedDate, startHour, startMinute, chosenDepartment.dayStart, grace);
			let endTime = isAffectByDayStart(selectedDate, endHour, endMinute, chosenDepartment.dayStart, grace);

			if (endTime <= startTime) endTime = endTime.plus({ day: 1 });

			return { startTime, endTime };
		}
		return {
			startTime: selectedDate.set({ hour: 6, minute: 0, second: 0, millisecond: 0 }),
			endTime: selectedDate.set({ hour: 18, minute: 0, second: 0, millisecond: 0 }),
		};
	});

	const [timePickerAnchorEl, setTimePickerAnchorEl] = useState<null | HTMLElement>(null);
	const closeTimePicker = useCallback(() => {
		setTimePickerAnchorEl(null);
	}, []);
	const editTimePickerClicked = useCallback(
		(event: React.MouseEvent<HTMLElement> | React.FormEvent<HTMLElement>) => {
			event.stopPropagation();
			if (timePickerAnchorEl) {
				closeTimePicker();
			} else if (event) {
				setTimePickerAnchorEl(event.currentTarget);
			}
		},
		[timePickerAnchorEl, closeTimePicker],
	);
	const startTimeValue = useMemo(() => shiftTimes.startTime.toFormat("h:mm a") ?? "", [shiftTimes.startTime]);
	const endTimeValue = useMemo(() => shiftTimes.endTime.toFormat("h:mm a") ?? "", [shiftTimes.endTime]);

	const shiftDurationString = useMemo(() => {
		return `${shiftTimes.startTime && shiftTimes.endTime ? `${Math.abs(shiftTimes.endTime.diff(shiftTimes.startTime, "hour").hours)} hours` : "0 hours"}`;
	}, [shiftTimes.startTime, shiftTimes.endTime]);

	const longShiftLimit = chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING;
	const showLongShiftWarning = useMemo(() => {
		if (!shiftTimes.startTime || !shiftTimes.endTime) return false;
		return shiftTimes.endTime.diff(shiftTimes.startTime, "hours").hours >= longShiftLimit;
	}, [shiftTimes.startTime, shiftTimes.endTime, longShiftLimit]);

	const handleTimePickerValueChange = useCallback(({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
		setShiftTimes((prevState) => ({
			...prevState,
			startTime: newStartTime,
			endTime: newEndTime,
		}));
	}, []);

	const onLocationDropdownChange = useCallback((selectedValue: DepartmentLocation | null | undefined) => {
		setSelectedLocation(selectedValue);
	}, []);

	const { addShift, isLoading } = useAddSingleShiftByDateAndNurse({
		selectedDate,
		onCloseCalled,
	});

	const hasPtoOverlap = useMemo(() => {
		if (!shiftTimes.startTime || !shiftTimes.endTime) return false;
		return nurseFromProfile.employeeSpecificPreferences.some((pref) => isInPTO(pref, shiftTimes.startTime, chosenDepartment.dayStart, shiftTimes.endTime));
	}, [nurseFromProfile.employeeSpecificPreferences, shiftTimes, chosenDepartment.dayStart]);

	const performAddShift = useCallback(async () => {
		await addShift(nurseFromProfile, shiftTimes.startTime, shiftTimes.endTime, selectedLocation?.location, undefined, undefined, selectedJobId);
	}, [nurseFromProfile, addShift, shiftTimes, selectedLocation, selectedJobId]);

	const {
		warningState,
		runChecks,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
	} = useShiftPredictionWarnings(performAddShift);

	const addShiftClicked = useCallback(async () => {
		await runChecks({
			nurseId: nurseFromProfile.id as number,
			startTime: shiftTimes.startTime,
			endTime: shiftTimes.endTime,
			isGlobalSalary: nurseFromProfile.isGlobalSalary,
			isAgency: isAgencyNurse(nurseFromProfile.employmentType),
			hasPto: hasPtoOverlap,
			locationName: selectedLocation?.location,
			employeeStatusWarning: buildEmployeeStatusWarning(nurseFromProfile, chosenDepartment.department_id, selectedDate),
		});
	}, [nurseFromProfile, shiftTimes, hasPtoOverlap, runChecks, selectedLocation, chosenDepartment.department_id, selectedDate]);

	return {
		locations,
		onLocationDropdownChange,
		selectedLocation,
		editTimePickerClicked,
		startTimeValue,
		endTimeValue,
		shiftDurationString,
		timePickerAnchorEl,
		shiftTimes,
		handleTimePickerValueChange,
		addShiftClicked,
		isLoading,
		addShift,
		selectedJobId,
		setSelectedJobId,
		nurseJobs,
		warningState,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
		longShiftDuration: chosenDepartment.longShiftDuration,
		showLongShiftWarning,
		longShiftLimit,
	};
};
